import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { useMyDashboard } from '@hooks/useMyDashboard';
import PageLayout from '@components/layout/PageLayout';
import Tabs from '@components/common/Tabs';
import type { TabItem } from '@components/common/Tabs';
import { LoadingState } from '@components/common/loading';
import { AiReportsTab } from '@components/features/reports';
import { MyCustomDashboard } from '@components/features/dashboard-widgets';
import styles from './ReportsPage.module.css';

const AI_REPORTS_TAB = 'ai-reports';
const CUSTOM_DASHBOARD_TAB = 'my-custom-dashboard';

const TAB_ITEMS: TabItem[] = [
  { key: AI_REPORTS_TAB, label: 'AI-Powered Reports' },
  { key: CUSTOM_DASHBOARD_TAB, label: 'My Custom Dashboard' }
];

/**
 * The Reports page: the user's AI-powered Report Widgets and, for a user whose roles have Dashboard Widgets, their
 * custom Dashboard. The chosen tab is kept in the `tab` query parameter, so it can be linked to and survives a reload.
 */
const ReportsPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: myDashboard, isLoading } = useMyDashboard(!!user);

  // A user whose roles have no Dashboard Widget has no Dashboard, so there is nothing to switch to.
  const hasCustomDashboard = (myDashboard?.available_widgets.length ?? 0) > 0;
  const requestedCustomDashboard = searchParams.get('tab') === CUSTOM_DASHBOARD_TAB;
  const activeTab = hasCustomDashboard && requestedCustomDashboard ? CUSTOM_DASHBOARD_TAB : AI_REPORTS_TAB;

  const selectTab = (tabKey: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tabKey);
    setSearchParams(next);
  };

  // A link straight to the Dashboard tab waits for the answer, instead of flashing the AI reports first.
  if (requestedCustomDashboard && isLoading) {
    return (
      <PageLayout>
        <LoadingState message="Loading..." spinnerSize="large" />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {hasCustomDashboard && (
        <div className={styles.tabs}>
          <Tabs items={TAB_ITEMS} activeTab={activeTab} onTabChange={selectTab} />
        </div>
      )}
      {activeTab === CUSTOM_DASHBOARD_TAB ? (
        <div className={styles.dashboardTab}>
          <MyCustomDashboard />
        </div>
      ) : (
        <AiReportsTab />
      )}
    </PageLayout>
  );
};

export default ReportsPage;
